import type { FilterService } from './filter.service';
import type { SearchService } from './search.service';
import type { UiStateService } from './ui-state.service';
import type { ScrollPositionService } from './scroll-position.service';

/** Desktop "Show rows" — keep in sync with desktop-tab-status-row options */
const DESKTOP_SHOW_ROWS_OPTIONS = new Set([10, 25, 50, 100, 150, 200, 250]);

export type PersonaListTab =
  | 'all'
  | 'load'
  | 'shunting'
  | 'transloading'
  | 'receiving'
  | 'transferred';

export interface PersonaListSnapshot {
  tab: PersonaListTab;
  filters: Record<string, string[]>;
  searchQuery: string;
  currentPage: number;
  itemsPerPage: number;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  passportId: string;
  scrollY?: number;
}

export interface PersonaListStateDeps {
  filterService: FilterService;
  searchService: SearchService;
  ui: UiStateService;
  scrollPosition: ScrollPositionService;
}

export function normalizeDesktopShowRows(size: number): number {
  return DESKTOP_SHOW_ROWS_OPTIONS.has(size) ? size : 10;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function isFiltersRecord(value: unknown): value is Record<string, string[]> {
  if (!isRecord(value)) {
    return false;
  }
  for (const v of Object.values(value)) {
    if (!isStringArray(v)) {
      return false;
    }
  }
  return true;
}

const PERSONA_TAB_SET = new Set<PersonaListTab>([
  'all',
  'load',
  'shunting',
  'transloading',
  'receiving',
  'transferred',
]);

function isPersonaListTab(value: unknown): value is PersonaListTab {
  return typeof value === 'string' && PERSONA_TAB_SET.has(value as PersonaListTab);
}

function isPersonaListSnapshot(value: unknown): value is PersonaListSnapshot {
  if (!isRecord(value)) {
    return false;
  }
  if (!isPersonaListTab(value['tab'])) {
    return false;
  }
  if (!isFiltersRecord(value['filters'])) {
    return false;
  }
  if (typeof value['searchQuery'] !== 'string') {
    return false;
  }
  if (typeof value['currentPage'] !== 'number' || !Number.isFinite(value['currentPage'])) {
    return false;
  }
  if (typeof value['itemsPerPage'] !== 'number' || !Number.isFinite(value['itemsPerPage'])) {
    return false;
  }
  if (typeof value['sortColumn'] !== 'string') {
    return false;
  }
  const dir = value['sortDirection'];
  if (dir !== 'asc' && dir !== 'desc') {
    return false;
  }
  if (typeof value['passportId'] !== 'string') {
    return false;
  }
  const scrollY = value['scrollY'];
  if (scrollY !== undefined && (typeof scrollY !== 'number' || !Number.isFinite(scrollY))) {
    return false;
  }
  return true;
}

export function peekExpectRestore(expectKey: string): boolean {
  try {
    return globalThis.sessionStorage?.getItem(expectKey) === '1';
  } catch {
    return false;
  }
}

export function hasListSnapshot(snapshotKey: string): boolean {
  try {
    const raw = globalThis.sessionStorage?.getItem(snapshotKey);
    return typeof raw === 'string' && raw.length > 0;
  } catch {
    return false;
  }
}

export function clearListRestoreState(snapshotKey: string, expectKey: string): void {
  try {
    globalThis.sessionStorage?.removeItem(snapshotKey);
    globalThis.sessionStorage?.removeItem(expectKey);
  } catch {
    // sessionStorage may be unavailable
  }
}

export function saveListSnapshot(snapshotKey: string, snapshot: PersonaListSnapshot): void {
  try {
    globalThis.sessionStorage?.setItem(snapshotKey, JSON.stringify(snapshot));
  } catch {
    // ignore
  }
}

export function parseListSnapshot(snapshotKey: string): PersonaListSnapshot | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(snapshotKey);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isPersonaListSnapshot(parsed)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function leavePersonaListState(
  deps: PersonaListStateDeps,
  snapshotKey: string,
  expectKey: string,
  route: string
): void {
  clearListRestoreState(snapshotKey, expectKey);
  deps.filterService.clear();
  deps.searchService.clear();
  deps.ui.setSearchOpen(false);
  deps.ui.setFiltersOpen(false);
  deps.ui.setSchedulerTabSelected('all');
  deps.scrollPosition.clearScrollPosition(route);
}
